/**
 * Centralized date formatting utilities for resume system
 * Consolidates all date formatting logic from multiple files
 * Eliminates duplications in ResumePreview.jsx, ModalBasedResume.jsx, etc.
 */

/**
 * Format date string to MM/YYYY format
 * Handles various input formats and special cases
 * @param {string|Date} dateStr - Date in various formats
 * @returns {string} - Formatted date in MM/YYYY format or "Present"
 */
export const formatDateToMMYYYY = (dateStr) => {
  if (!dateStr) return "";

  // Handle "Present", "Current", "Ongoing" cases
  const cleanDate = dateStr.toString().trim().toLowerCase();
  if (
    cleanDate === "present" ||
    cleanDate === "current" ||
    cleanDate === "present" ||
    cleanDate === "current" ||
    cleanDate === "ongoing"
  ) {
    return "Present";
  }

  try {
    // Check if already in MM/YYYY or MM-YYYY format
    if (/^\d{2}[-/]\d{4}$/.test(cleanDate)) {
      return cleanDate.replace("-", "/"); // Normalize to MM/YYYY
    }

    // Check if it's a year only (YYYY)
    if (/^\d{4}$/.test(cleanDate)) {
      return `01/${cleanDate}`; // Default to January
    }

    // Try to parse the date
    const date = new Date(dateStr);

    if (date && !isNaN(date.getTime())) {
      // Format as MM/YYYY
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      return `${month}/${year}`;
    }

    // If all parsing fails, return original string
    return dateStr;
  } catch (error) {
    console.warn("Date formatting error:", dateStr, error);
    return dateStr;
  }
};

/**
 * Parse a date string and convert it to a comparable Date object
 * Enhanced version from DateSortingUtils.js with additional format support
 * @param {string} dateStr - Date string (e.g., "2023", "Jan 2023", "2023-01", "Present")
 * @returns {Date} - Date object for comparison
 */
export const parseResumeDate = (dateStr) => {
  if (!dateStr) return new Date(0); // Very old date for missing dates

  const cleanDate = dateStr.toString().trim().toLowerCase();

  // Handle "present", "current", "ongoing" cases
  if (cleanDate === "present" || cleanDate === "current" || cleanDate === "ongoing") {
    return new Date(); // Current date
  }

  // Handle year-only format (e.g., "2023")
  if (/^\d{4}$/.test(cleanDate)) {
    return new Date(parseInt(cleanDate), 0, 1); // January 1st of that year
  }

  // Handle month-year formats (e.g., "Jan 2023", "January 2023", "01/2023", "2023-01")
  const monthYearPatterns = [
    /^(\w+)\s+(\d{4})$/, // "Jan 2023", "January 2023"
    /^(\d{1,2})\/(\d{4})$/, // "01/2023"
    /^(\d{4})-(\d{1,2})$/, // "2023-01"
    /^(\d{1,2})-(\d{4})$/, // "01-2023"
  ];

  for (const pattern of monthYearPatterns) {
    const match = cleanDate.match(pattern);
    if (match) {
      const [, part1, part2] = match;

      // Determine which part is month and which is year
      let month, year;

      if (pattern === monthYearPatterns[0]) {
        // Month name format
        month = parseMonthName(part1);
        year = parseInt(part2);
      } else if (pattern === monthYearPatterns[2]) {
        // YYYY-MM format
        year = parseInt(part1);
        month = parseInt(part2) - 1; // Month is 0-indexed in JavaScript
      } else {
        // MM/YYYY or MM-YYYY format
        month = parseInt(part1) - 1; // Month is 0-indexed in JavaScript
        year = parseInt(part2);
      }

      if (isValidMonth(month) && isValidYear(year)) {
        return new Date(year, month, 1);
      }
    }
  }

  // Try to parse as a standard date
  const standardDate = new Date(dateStr);
  if (!isNaN(standardDate.getTime())) {
    return standardDate;
  }

  // If all parsing fails, return a very old date
  console.warn(`Could not parse date: ${dateStr}`);
  return new Date(0);
};

/**
 * Format a date range (startDate - endDate)
 * Commonly used in work experience, education, etc.
 * @param {string} startDate - Start date
 * @param {string} endDate - End date
 * @param {boolean} isCurrentJob - Whether this is current/ongoing
 * @returns {string} - Formatted date range (e.g., "01/2020 - Present")
 */
export const formatDateRange = (startDate, endDate, isCurrentJob = false) => {
  const start = formatDateToMMYYYY(startDate);
  const end =
    isCurrentJob || endDate === "Present" || endDate === "Current" || !endDate
      ? "Present"
      : formatDateToMMYYYY(endDate);

  return `${start} - ${end}`;
};

/**
 * Format date for display in various contexts
 * More flexible than MM/YYYY - can handle full dates
 * @param {string|Date} dateStr - Date to format
 * @param {string} format - Format type: 'short' (MM/YYYY), 'medium' (Mon YYYY), 'full'
 * @returns {string} - Formatted date
 */
export const formatDateForDisplay = (dateStr, format = "short") => {
  if (!dateStr) return "";

  const cleanDate = dateStr.toString().trim().toLowerCase();
  if (cleanDate === "present" || cleanDate === "current" || cleanDate === "ongoing") {
    return "Present";
  }

  try {
    const date = parseResumeDate(dateStr);

    if (format === "short") {
      return formatDateToMMYYYY(dateStr);
    }

    if (format === "medium") {
      return date.toLocaleDateString("en-US", {
        month: "short",
        year: "numeric",
      });
    }

    if (format === "full") {
      return date.toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      });
    }

    return formatDateToMMYYYY(dateStr);
  } catch (error) {
    console.warn("Date display formatting error:", dateStr, error);
    return dateStr;
  }
};

/**
 * Sort dates in descending order (most recent first)
 * Useful for experience, education sorting
 * @param {string} dateA - First date
 * @param {string} dateB - Second date
 * @returns {number} - Sort comparison result
 */
export const sortDatesDescending = (dateA, dateB) => {
  const parsedA = parseResumeDate(dateA);
  const parsedB = parseResumeDate(dateB);
  return parsedB.getTime() - parsedA.getTime();
};

/**
 * Check if a date string represents "current" or "present"
 * @param {string} dateStr - Date string to check
 * @returns {boolean} - True if represents current/present
 */
export const isCurrentDate = (dateStr) => {
  if (!dateStr) return false;
  const cleanDate = dateStr.toString().trim().toLowerCase();
  return ["present", "current", "ongoing"].includes(cleanDate);
};

// Helper functions (internal use)

/**
 * Parse month name to month index (0-11)
 * @param {string} monthName - Month name (e.g., "Jan", "January")
 * @returns {number} - Month index (0-11)
 */
const parseMonthName = (monthName) => {
  const monthNames = {
    jan: 0,
    january: 0,
    feb: 1,
    february: 1,
    mar: 2,
    march: 2,
    apr: 3,
    april: 3,
    may: 4,
    jun: 5,
    june: 5,
    jul: 6,
    july: 6,
    aug: 7,
    august: 7,
    sep: 8,
    september: 8,
    oct: 9,
    october: 9,
    nov: 10,
    november: 10,
    dec: 11,
    december: 11,
  };

  return monthNames[monthName.toLowerCase()] ?? 0;
};

/**
 * Check if month is valid (0-11)
 * @param {number} month - Month index
 * @returns {boolean} - Is valid month
 */
const isValidMonth = (month) => {
  return month >= 0 && month <= 11;
};

/**
 * Check if year is valid (reasonable range)
 * @param {number} year - Year
 * @returns {boolean} - Is valid year
 */
const isValidYear = (year) => {
  const currentYear = new Date().getFullYear();
  return year >= 1900 && year <= currentYear + 10; // Allow future dates up to 10 years
};

// Default export for convenience
export default {
  formatDateToMMYYYY,
  parseResumeDate,
  formatDateRange,
  formatDateForDisplay,
  sortDatesDescending,
  isCurrentDate,
};
